export default {
    /**
     * Fetch the products and required data from the api.
     *
     * @param {object} context
     * @param {object} payload
     * @param {object} payload.filters
     * @param {boolean} payload.onlyFetchProducts
     * @param {string} payload.locale
     *
     * @returns {Promise<{menuLinks: {name: string, to: string}[], categories: object[], products: object[]}>}
     */
    async fetchProducts(context, {
        filters = {},
        onlyFetchProducts = false,
        locale,
    } = {}) {
        const queryFilters = {
            // By default we want to exclude projects that don't yet have a
            // page link, as they can't actually be linked to
            page: {
                documentId: {
                    notNull: true,
                },
            },
            publishedAt: {
                notNull: true,
            },
            // Merge the provided additional filters in
            ...filters,
        };

        // Import the combined query for fetching products for the product listing page
        const { default: productsQuery } = await import('../../queries/grocery/grocery-products-listing.graphql');

        // Run the query
        const {
            groceryProducts,
            groceryProductCategories,
            navigationGroups,
        } = await this.$strapi.query(productsQuery, {
            filters: queryFilters,
            onlyFetchProducts,
            locale,
        });

        const {
            formatMenuLinks,
            formatProductCategories,
            formatProducts,
        } = await import('../../lib/products');

        // Return the formatted data
        return {
            menuLinks: formatMenuLinks(navigationGroups?.[0]?.navigation_items),
            categories: formatProductCategories(groceryProductCategories),
            products: formatProducts(this, groceryProducts),
        };
    },

    /**
     * Fetch the grocery categories.
     *
     * @param {object} context
     * @param {object} payload
     * @param {string} payload.locale
     *
     * @returns {Promise<{text: string, to: string}[]>}
     */
    async fetchCategories(context, {
        locale,
    }) {
        // Import the combined query for fetching grocery product categories
        const { default: categoriesQuery } = await import('../../queries/grocery/grocery-product-categories.graphql');

        // Run the query
        const { groceryProductCategories } = await this.$strapi.query(categoriesQuery, {
            locale,
        });

        const { formatProductCategories } = await import('../../lib/products');

        // Return the formatted data
        return formatProductCategories(groceryProductCategories);
    },
};
